import { PromptsFiltersParams } from '@/components/prompts/PromptsPageContent';
import PromptInstruction from '@/models/PromptInstruction';
import Observable from '@/utils/observers/ObserverPattern';
import TreePrompt from '@/utils/trees/TreePrompt';
import useFilterParams from '@/utils/UseFilterParams';
import {
  createContext,
  MutableRefObject,
  PropsWithChildren,
  useMemo,
  useRef,
} from 'react';

type InsertArgs = {
  prompt: PromptInstruction;
  parentElementId?: number;
};
export interface PromptTreeContextProps {
  tree: MutableRefObject<TreePrompt>;
  getPath: (id: number) => number[] | undefined;
  insert: (args: InsertArgs) => void;
  pathParams: number[];
  cacheObserver: Observable<number, PromptInstruction>;
}

export const PromptTreeContext = createContext<PromptTreeContextProps>(
  {} as PromptTreeContextProps,
);

export default function PromptTreeProvider({ children }: PropsWithChildren) {
  const { filterParams } = useFilterParams<PromptsFiltersParams>();
  const tree = useRef<TreePrompt>(
    new TreePrompt({
      id: 0,
      category: '' as never,
      instruction: '',
      role: null,
      createdAt: '',
      updatedAt: '',
    }),
  );
  const getPath = (id: number) => {
    const path = tree.current.findPath(id);
    return path?.map(p => p.id);
  };

  const pathParams = filterParams.getAll('path')?.map(Number) || [];

  const insert = ({ prompt, parentElementId }: InsertArgs) => {
    tree.current.insert(prompt, parentElementId);
  };

  const cacheObserver = useMemo(
    () => new Observable<number, PromptInstruction>(),
    [],
  );

  return (
    <PromptTreeContext.Provider
      value={{
        tree,
        getPath,
        insert,
        pathParams,
        cacheObserver,
      }}
    >
      {children}
    </PromptTreeContext.Provider>
  );
}
